import { Logo } from '@components/layout/Logo';
import { ScrollReveal } from '@components/motion/ScrollReveal';
import { StaggerContainer, StaggerItem } from '@components/motion/StaggerContainer';
import { ROUTES } from '@constants/routes';
import { COMPANY, whatsappUrl } from '@constants/company';
import {
  FooterWrapper,
  FooterContainer,
  FooterTop,
  BrandColumn,
  BrandTagline,
  SocialRow,
  SocialLink,
  LinkColumn,
  ColumnTitle,
  FooterLink,
  FooterExternalLink,
  ContactItem,
  ContactLabel,
  ContactValue,
  FooterBottom,
  Copyright,
  FooterBadge,
  AccentDot,
} from './styles';

/* ── Ícones SVG inline (sem dependência externa) ────────────── */

function IconInstagram() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="2" y="2" width="20" height="20" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

function IconFacebook() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
    </svg>
  );
}

function IconLinkedIn() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
      <rect x="2" y="9" width="4" height="12" />
      <circle cx="4" cy="4" r="2" />
    </svg>
  );
}

function IconWhatsApp() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
      <path d="M11.5 2C6.253 2 2 6.253 2 11.5c0 1.807.49 3.497 1.343 4.957L2 22l5.688-1.323A9.44 9.44 0 0 0 11.5 21C16.747 21 21 16.747 21 11.5S16.747 2 11.5 2zm0 17.198a7.697 7.697 0 0 1-3.963-1.098l-.284-.169-2.95.686.697-2.873-.185-.295A7.697 7.697 0 0 1 3.802 11.5c0-4.245 3.453-7.698 7.698-7.698S19.198 7.255 19.198 11.5 15.745 19.198 11.5 19.198z" />
    </svg>
  );
}

const eventLinks = [
  { label: 'FEPRO', slug: 'fepro' },
  // { label: 'Salão Noivas & Festas', slug: 'salao-noivas-festas' },
  { label: 'Nossa Feira', slug: 'nossa-feira' },
  { label: 'Festa do Morango', slug: 'festa-do-morango' },
  { label: 'Festa da Flor', slug: 'festa-da-flor' },
  // { label: 'Festival Comida de Boteco', slug: 'festival-comida-de-boteco' },
];

const year = new Date().getFullYear();

/**
 * Footer global da aplicação.
 * Grid de 4 colunas no desktop, 1 coluna no mobile.
 * Animações de reveal ao entrar no viewport.
 */
export function Footer() {
  return (
    <FooterWrapper>
      <FooterContainer>
        <FooterTop>
          {/* ── Coluna da marca ───────────────────────────── */}
          <ScrollReveal direction="up" delay={0}>
            <BrandColumn>
              <Logo variant="inverse" size="md" />
              <BrandTagline>
                Há mais de 26 anos conectando pessoas através de eventos que marcam. Planejamento,
                organização e execução com excelência em Nova Friburgo e região.
              </BrandTagline>
              <SocialRow>
                <SocialLink
                  href={COMPANY.social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram da Teia de Eventos"
                >
                  <IconInstagram />
                </SocialLink>
                <SocialLink
                  href={COMPANY.social.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook da Teia de Eventos"
                >
                  <IconFacebook />
                </SocialLink>
                <SocialLink
                  href={COMPANY.social.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn da Teia de Eventos"
                >
                  <IconLinkedIn />
                </SocialLink>
                <SocialLink
                  href={whatsappUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="WhatsApp da Teia de Eventos"
                >
                  <IconWhatsApp />
                </SocialLink>
              </SocialRow>
            </BrandColumn>
          </ScrollReveal>

          {/* ── Navegação ────────────────────────────────── */}
          <ScrollReveal direction="up" delay={0.1}>
            <LinkColumn>
              <ColumnTitle>Navegação</ColumnTitle>
              <FooterLink to={ROUTES.home}>Início</FooterLink>
              <FooterLink to={ROUTES.about}>Quem Somos</FooterLink>
              <FooterLink to={ROUTES.events}>Eventos</FooterLink>
            </LinkColumn>
          </ScrollReveal>

          {/* ── Eventos ──────────────────────────────────── */}
          <ScrollReveal direction="up" delay={0.15}>
            <LinkColumn>
              <ColumnTitle>Nossos Eventos</ColumnTitle>
              <StaggerContainer staggerDelay={0.04} initialDelay={0.2}>
                {eventLinks.map(({ label, slug }) => (
                  <StaggerItem key={slug} direction="none">
                    <FooterExternalLink as={FooterLink} to={`/eventos/${slug}`}>
                      {label}
                    </FooterExternalLink>
                  </StaggerItem>
                ))}
              </StaggerContainer>
            </LinkColumn>
          </ScrollReveal>

          {/* ── Contato ──────────────────────────────────── */}
          <ScrollReveal direction="up" delay={0.2}>
            <LinkColumn>
              <ColumnTitle>Contato</ColumnTitle>
              <ContactItem>
                <ContactLabel>Telefone</ContactLabel>
                <ContactValue href={`tel:${COMPANY.contact.phoneDigits}`}>
                  {COMPANY.contact.phone}
                </ContactValue>
              </ContactItem>
              <ContactItem>
                <ContactLabel>WhatsApp</ContactLabel>
                <ContactValue href={whatsappUrl()} target="_blank" rel="noopener noreferrer">
                  {COMPANY.contact.whatsapp}
                </ContactValue>
              </ContactItem>
              <ContactItem>
                <ContactLabel>E-mail</ContactLabel>
                <ContactValue href={`mailto:${COMPANY.contact.email}`}>
                  {COMPANY.contact.email}
                </ContactValue>
              </ContactItem>
              <ContactItem>
                <ContactLabel>Endereço</ContactLabel>
                <ContactValue as="address" style={{ fontStyle: 'normal' }}>
                  {COMPANY.address.full}
                </ContactValue>
              </ContactItem>
            </LinkColumn>
          </ScrollReveal>
        </FooterTop>

        {/* ── Rodapé inferior ─────────────────────────────── */}
        <FooterBottom>
          <Copyright>
            © {year} {COMPANY.name} — {COMPANY.legalName}. Todos os direitos reservados.
          </Copyright>
          <FooterBadge>
            <AccentDot aria-hidden />
            Nova Friburgo, RJ
          </FooterBadge>
        </FooterBottom>
      </FooterContainer>
    </FooterWrapper>
  );
}
